/**
 * One per-person connector and the person's own connection to it (item 65A)
 * — the same control in Settings → Connectors, the composer's connectors
 * panel and a chat's "Connect your <service> account" prompt.
 *
 * Signing in with an account opens the service in a popup; a per-person key
 * is typed here and stored encrypted (never shown again). Disconnect revokes
 * at the service. A paused connection says why and keeps Disconnect; one the
 * platform removed says so once.
 */

import { useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import { errorBody } from '../services/connectorLibraryService';
import {
  myConnectorsService, type ConnectOutcome, type ConnectWait, type KeyCredential, type MyConnector,
} from '../services/myConnectorsService';
import { ServiceIcon } from './LibraryBits';
import './PersonalConnect.css';

interface Props {
  connector: MyConnector;
  /** Something changed: the caller reads the list again. */
  onChanged: () => void;
  /** 'row' in Settings, 'compact' in the composer panel. */
  variant?: 'row' | 'compact';
}

/**
 * Signing in with an account, for one control: the popup, the wait for its
 * answer (with Cancel — a closed popup is never taken as one), and what the
 * person is told when it ends.
 */
export function useAccountConnect(name: string, onDone?: (outcome: ConnectOutcome) => void) {
  const { t } = useLanguage();
  const [wait, setWait] = useState<ConnectWait | null>(null);
  const start = (entryId: string) => {
    const w = myConnectorsService.connectWithAccount(entryId);   // inside the click: the popup opens now
    setWait(w);
    void w.outcome
      .then((outcome) => {
        if (outcome === 'connected') showNotification(t('myConnectors.connected', { name }), 'success');
        else if (outcome === 'blocked') showNotification(t('myConnectors.popupBlocked'), 'error');
        else if (outcome !== 'cancelled') showNotification(t(`myConnectors.landing.${outcome}`, { name }), 'error');
        onDone?.(outcome);
      })
      .catch((err) => {
        const body = errorBody(err);
        showNotification(body.error_code === 'OAUTH_START_HOST' ? t('cloudStorage.wrongHost')
          : body.error || t('myConnectors.error'), 'error');
      })
      .finally(() => setWait(null));
  };
  return { waiting: wait !== null, start, cancel: () => wait?.cancel() };
}

/** "Finish signing in in the window that opened" — and Cancel. */
export function ConnectWaiting({ onCancel, testId }: { onCancel: () => void; testId: string }) {
  const { t } = useLanguage();
  return (
    <span className="pcx__waiting" role="status" data-testid={testId}>
      <span>{t('myConnectors.waiting')}</span>
      <button type="button" className="cx-btn cx-btn--sm" onClick={onCancel}>{t('common.cancel')}</button>
    </span>
  );
}

export function PersonalConnect({ connector: c, onChanged, variant = 'row' }: Props) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [askKey, setAskKey] = useState(false);
  const account = useAccountConnect(c.name, () => onChanged());

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      showNotification(errorBody(err).error || t('myConnectors.error'), 'error');
    } finally { setBusy(false); }
  };

  const connect = () => {
    if (c.signin_mode === 'per_person_key') { setAskKey(true); return; }
    account.start(c.entry_id);
  };

  const disconnect = () => run(async () => {
    await myConnectorsService.disconnect(c.entry_id);
    showNotification(t('myConnectors.disconnected', { name: c.name }), 'success');
    onChanged();
  });

  const dismiss = () => run(async () => { await myConnectorsService.dismiss(c.entry_id); onChanged(); });

  const statusText = (() => {
    switch (c.status) {
      case 'connected':
        return c.account_label ? t('myConnectors.status.connectedAs', { account: c.account_label }) : t('myConnectors.status.connected');
      case 'paused':
        return t(c.paused_reason === 'deleted' ? 'myConnectors.status.pausedDeleted' : 'myConnectors.status.paused');
      case 'expired':
        return t('myConnectors.status.expired');
      case 'removed':
        return t(c.removed_reason === 'deactivated' ? 'myConnectors.status.removedDeactivated' : 'myConnectors.status.removed');
      default:
        return t('myConnectors.status.notConnected');
    }
  })();

  return (
    <div className={`pcx pcx--${variant} pcx--${c.status}`} data-testid={`my-connector-${c.slug}`}>
      <ServiceIcon name={c.name} slug={c.slug} url={c.url} />
      <div className="pcx__text">
        <span className="pcx__name"><bdi dir="auto">{c.name}</bdi></span>
        <span className="pcx__status" data-status={c.status}>
          {c.status === 'connected' && <span className="pcx__dot" aria-hidden="true" />}
          {statusText}
        </span>
        {variant === 'row' && c.description && <span className="pcx__desc" dir="auto">{c.description}</span>}
      </div>
      <div className="pcx__actions">
        {(c.status === 'not_connected' || c.status === 'expired') && !askKey && (account.waiting ? (
          <ConnectWaiting onCancel={account.cancel} testId={`my-connector-waiting-${c.slug}`} />
        ) : (
          <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" disabled={busy} onClick={connect}
                  data-testid={`my-connector-connect-${c.slug}`}>
            {c.status === 'expired' ? t('myConnectors.reconnect') : t('myConnectors.connect')}
          </button>
        ))}
        {(c.status === 'connected' || c.status === 'paused') && (
          <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => { void disconnect(); }}
                  data-testid={`my-connector-disconnect-${c.slug}`}>
            {t('myConnectors.disconnect')}
          </button>
        )}
        {c.status === 'removed' && (
          <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => { void dismiss(); }}>
            {t('myConnectors.gotIt')}
          </button>
        )}
      </div>
      {askKey && (
        <KeyForm connector={c} busy={busy} onCancel={() => setAskKey(false)}
                 onSave={credential => run(async () => {
                   await myConnectorsService.saveKey(c.entry_id, credential);
                   setAskKey(false);
                   showNotification(t('myConnectors.keySaved', { name: c.name }), 'success');
                   onChanged();
                 })} />
      )}
    </div>
  );
}

export function KeyForm({ connector, busy, onSave, onCancel }: {
  connector: MyConnector; busy: boolean; onSave: (c: KeyCredential) => void; onCancel: () => void;
}) {
  const { t } = useLanguage();
  const basic = connector.key_shape?.auth_type === 'basic';
  const [value, setValue] = useState('');
  const [username, setUsername] = useState('');
  const [label, setLabel] = useState('');
  const ready = basic ? !!username.trim() && !!value : !!value.trim();
  return (
    <form className="pcx__key" onSubmit={e => { e.preventDefault(); if (ready) onSave(basic ? { username, password: value, label } : { value, label }); }}>
      <p className="pcx__key-hint">{t('myConnectors.keyHint', { name: connector.name })}</p>
      {basic && (
        <input className="form-input" value={username} onChange={e => setUsername(e.target.value)} dir="ltr"
               placeholder={t('myConnectors.username')} aria-label={t('myConnectors.username')} autoComplete="off" />
      )}
      <input className="form-input" type="password" value={value} onChange={e => setValue(e.target.value)} dir="ltr"
             placeholder={basic ? t('myConnectors.password') : t('myConnectors.key')}
             aria-label={basic ? t('myConnectors.password') : t('myConnectors.key')} autoComplete="off"
             data-testid={`my-connector-key-${connector.slug}`} />
      <input className="form-input" value={label} onChange={e => setLabel(e.target.value)} dir="auto"
             placeholder={t('myConnectors.labelPlaceholder')} aria-label={t('myConnectors.labelPlaceholder')} />
      <div className="pcx__key-actions">
        <button type="button" className="cx-btn cx-btn--sm" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="submit" className="cx-btn cx-btn--primary cx-btn--sm" disabled={busy || !ready}
                data-testid={`my-connector-key-save-${connector.slug}`}>{t('myConnectors.saveKey')}</button>
      </div>
    </form>
  );
}
